import type { ReactNode } from "react";
import React from "react";

import { useHotkeyPermissions } from "../hooks/useHotkeyPermissions";
import type { HotkeyBadgeProps, CommandPaletteProps, HelpDialogProps } from "../types";
import { HotkeyBadge } from "./HotkeyBadge";
import { CommandPalette } from "./CommandPalette";
import { HelpDialog } from "./HelpDialog";
import { DISPLAY_NAMES } from "../constants";

/**
 * Permission-aware HotkeyBadge that only renders if the module has hotkey permissions.
 * Use this when you want badges to automatically hide in modules without hotkey access.
 *
 * @example
 * ```tsx
 * // Automatically hidden if module lacks hotkey permissions
 * <PermissionAwareHotkeyBadge combo="CTRL+S" />
 *
 * // Show fallback content instead of badge
 * <PermissionAwareHotkeyBadge combo="CTRL+S" fallback={<span>Save</span>} />
 * ```
 */
export interface PermissionAwareHotkeyBadgeProps extends HotkeyBadgeProps {
  /** Content to render if module lacks hotkey permissions. Defaults to null. */
  fallback?: ReactNode;
  /** If true, shows the badge even without permissions (for documentation/preview). */
  forceShow?: boolean;
}

export function PermissionAwareHotkeyBadge({
  fallback = null,
  forceShow = false,
  ...badgeProps
}: PermissionAwareHotkeyBadgeProps): ReactNode {
  const { canRegister } = useHotkeyPermissions();

  // Show badge if forced or if module has permission
  if (forceShow || canRegister) {
    return <HotkeyBadge {...badgeProps} />;
  }

  // Module lacks permission - render fallback
  return <>{fallback}</>;
}

PermissionAwareHotkeyBadge.displayName = DISPLAY_NAMES.HotkeyBadge + ".PermissionAware";

/**
 * Permission-aware CommandPalette that only renders if the module has hotkey permissions.
 * Prevents command palette from appearing in modules that shouldn't have hotkey access.
 *
 * @example
 * ```tsx
 * // Only renders if module has hotkey permissions
 * <PermissionAwareCommandPalette isOpen={isOpen} onOpenChange={setIsOpen} />
 * ```
 */
export interface PermissionAwareCommandPaletteProps extends CommandPaletteProps {
  /** If true, shows the palette even without permissions (for documentation/preview). */
  forceShow?: boolean;
}

export function PermissionAwareCommandPalette({
  forceShow = false,
  ...paletteProps
}: PermissionAwareCommandPaletteProps): ReactNode {
  const { canRegister } = useHotkeyPermissions();

  // Don't render palette if module lacks permission (unless forced)
  if (!forceShow && !canRegister) {
    return null;
  }

  return <CommandPalette {...paletteProps} />;
}

PermissionAwareCommandPalette.displayName = "CommandPalette.PermissionAware";

/**
 * Permission-aware HelpDialog that only renders if the module has hotkey permissions.
 * Prevents help dialog from appearing in modules that shouldn't have hotkey access.
 *
 * @example
 * ```tsx
 * // Only renders if module has hotkey permissions
 * <PermissionAwareHelpDialog isOpen={isOpen} onOpenChange={setIsOpen} />
 * ```
 */
export interface PermissionAwareHelpDialogProps extends HelpDialogProps {
  /** If true, shows the dialog even without permissions (for documentation/preview). */
  forceShow?: boolean;
}

export function PermissionAwareHelpDialog({
  forceShow = false,
  ...dialogProps
}: PermissionAwareHelpDialogProps): ReactNode {
  const { canRegister } = useHotkeyPermissions();

  // Don't render dialog if module lacks permission (unless forced)
  if (!forceShow && !canRegister) {
    return null;
  }

  return <HelpDialog {...dialogProps} />;
}

PermissionAwareHelpDialog.displayName = "HelpDialog.PermissionAware";

/**
 * Hook to conditionally render hotkey-related UI based on permissions.
 * Useful for custom components that need permission awareness.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { shouldShowHotkeyUI, permissions } = useHotkeyUIPermissions();
 *
 *   return (
 *     <div>
 *       {shouldShowHotkeyUI && <HotkeyBadge combo="CTRL+S" />}
 *       <span>Save</span>
 *     </div>
 *   );
 * }
 * ```
 */
export function useHotkeyUIPermissions() {
  const permissions = useHotkeyPermissions();

  return {
    /** Whether hotkey UI elements should be shown */
    shouldShowHotkeyUI: permissions.canRegister,
    /** Whether global scope hotkey UI should be shown */
    shouldShowGlobalHotkeyUI: permissions.canUseGlobalScope,
    /** Full permissions object for advanced use cases */
    permissions,
  };
}

/**
 * Higher-order component to wrap any component with permission awareness.
 * The wrapped component only renders if the module has hotkey permissions.
 *
 * @example
 * ```tsx
 * const PermissionAwareMyComponent = withHotkeyPermissions(MyComponent);
 * // or with fallback
 * const PermissionAwareMyComponent = withHotkeyPermissions(MyComponent, <div>No access</div>);
 * ```
 */
export function withHotkeyPermissions<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  fallback: ReactNode = null,
): React.FC<P & { forceShow?: boolean }> {
  const WithPermissions: React.FC<P & { forceShow?: boolean }> = ({ forceShow, ...props }) => {
    const { canRegister } = useHotkeyPermissions();

    if (!forceShow && !canRegister) {
      return <>{fallback}</>;
    }

    return <WrappedComponent {...(props as P)} />;
  };

  WithPermissions.displayName = `withHotkeyPermissions(${WrappedComponent.displayName || WrappedComponent.name || "Component"})`;

  return WithPermissions;
}
